import React, { useState } from "react";

interface RibbonBarProps {
  onFormatChange?: (format: string, value: any) => void;
  onInsertRow?: () => void;
  onInsertColumn?: () => void;
  onDeleteRow?: () => void;
  onDeleteColumn?: () => void;
  selectedCell?: { row: number; col: number };
  selectedRange?: { start: { row: number; col: number }; end: { row: number; col: number } } | null;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

const menuItems = [
  "File",
  "Edit",
  "View",
  "Insert",
  "Format",
  "Data",
  "Tools",
  "Extensions",
  "Help"
];

const RibbonBar: React.FC<RibbonBarProps> = ({
  onFormatChange,
  onInsertRow,
  onInsertColumn,
  onDeleteRow,
  onDeleteColumn,
  selectedCell,
  selectedRange,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false
}) => {
  const [fontSize, setFontSize] = useState("10");
  const [fontFamily, setFontFamily] = useState("Default");
  const [fontColor, setFontColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [alignment, setAlignment] = useState("left");

  const handleFormatChange = (type: string, value: any) => {
    onFormatChange?.(type, value);
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      {/* Menu Bar */}
      <div className="flex items-center px-2 h-10 text-sm select-none">
        <div className="flex items-center mr-2">
          <svg className="w-7 h-7 mr-1" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" fill="#22c55e"/><rect x="7" y="7" width="10" height="10" rx="1" fill="#fff"/></svg>
          <span className="font-medium text-lg text-gray-800">Untitled spreadsheet</span>
          <span className="ml-1 text-gray-400 text-base">★</span>
        </div>
        {menuItems.map((item) => (
          <button key={item} className="px-2 py-1 rounded hover:bg-gray-100 text-gray-700 font-normal focus:outline-none">
            {item}
          </button>
        ))}
      </div>
      {/* Toolbar */}
      <div className="flex items-center px-2 py-1 space-x-1 border-b border-gray-200 bg-white">
        {/* Undo/Redo */}
        <button onClick={onUndo} disabled={!canUndo} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50" title="Undo (Ctrl+Z)">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
        </button>
        <button onClick={onRedo} disabled={!canRedo} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50" title="Redo (Ctrl+Y)">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
        </button>
        {/* Print */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Print">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="6" y="9" width="12" height="7" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M6 17v2a2 2 0 002 2h8a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2"/><path d="M6 9V7a2 2 0 012-2h8a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2"/></svg>
        </button>
        {/* Zoom */}
        <select className="border border-gray-300 rounded px-1 py-0.5 text-xs w-16 focus:outline-none" defaultValue="100%">
          <option>50%</option>
          <option>75%</option>
          <option>90%</option>
          <option>100%</option>
          <option>125%</option>
          <option>150%</option>
        </select>
        {/* Currency, Percent, Decimal */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Format as currency">
          <span className="font-semibold">$</span>
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Format as percent">
          <span className="font-semibold">%</span>
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Decrease decimal places">
          <span className="font-semibold">.0↓</span>
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Increase decimal places">
          <span className="font-semibold">.0↑</span>
        </button>
        {/* Font family */}
        <select value={fontFamily} onChange={e => { setFontFamily(e.target.value); handleFormatChange("fontFamily", e.target.value); }} className="border border-gray-300 rounded px-1 py-0.5 text-xs w-24 focus:outline-none">
          <option>Default</option>
          <option>Arial</option>
          <option>Calibri</option>
          <option>Times New Roman</option>
        </select>
        {/* Font size */}
        <input type="number" min="6" max="72" value={fontSize} onChange={e => { setFontSize(e.target.value); handleFormatChange("fontSize", e.target.value); }} className="border border-gray-300 rounded px-1 py-0.5 text-xs w-12 focus:outline-none" />
        {/* Bold, Italic, Underline */}
        <button className={`p-2 rounded ${isBold ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setIsBold(!isBold); handleFormatChange("bold", !isBold); }} title="Bold (Ctrl+B)"><b>B</b></button>
        <button className={`p-2 rounded ${isItalic ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setIsItalic(!isItalic); handleFormatChange("italic", !isItalic); }} title="Italic (Ctrl+I)"><i>I</i></button>
        <button className={`p-2 rounded ${isUnderline ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setIsUnderline(!isUnderline); handleFormatChange("underline", !isUnderline); }} title="Underline (Ctrl+U)"><u>U</u></button>
        {/* Text color */}
        <input type="color" value={fontColor} onChange={e => { setFontColor(e.target.value); handleFormatChange("fontColor", e.target.value); }} className="w-6 h-6 border border-gray-300 rounded cursor-pointer hover:border-gray-400" title="Text color" />
        {/* Fill color */}
        <input type="color" value={backgroundColor} onChange={e => { setBackgroundColor(e.target.value); handleFormatChange("backgroundColor", e.target.value); }} className="w-6 h-6 border border-gray-300 rounded cursor-pointer hover:border-gray-400" title="Fill color" />
        {/* Borders */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Borders">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M4 8h16M4 16h16M8 4v16M16 4v16" stroke="currentColor" strokeWidth="2"/></svg>
        </button>
        {/* Alignment */}
        <button className={`p-2 rounded ${alignment === 'left' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setAlignment('left'); handleFormatChange("alignment", 'left'); }} title="Align left">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="2"/><rect x="3" y="10" width="12" height="2"/><rect x="3" y="14" width="18" height="2"/><rect x="3" y="18" width="12" height="2"/></svg>
        </button>
        <button className={`p-2 rounded ${alignment === 'center' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setAlignment('center'); handleFormatChange("alignment", 'center'); }} title="Align center">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="2"/><rect x="3" y="10" width="18" height="2"/><rect x="6" y="14" width="12" height="2"/><rect x="3" y="18" width="18" height="2"/></svg>
        </button>
        <button className={`p-2 rounded ${alignment === 'right' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`} onClick={() => { setAlignment('right'); handleFormatChange("alignment", 'right'); }} title="Align right">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="2"/><rect x="9" y="10" width="12" height="2"/><rect x="3" y="14" width="18" height="2"/><rect x="9" y="18" width="12" height="2"/></svg>
        </button>
        {/* Insert Link */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Insert link">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07l-1.41 1.41" stroke="currentColor" strokeWidth="2"/><path d="M14 11a5 5 0 00-7.07 0l-1.41 1.41a5 5 0 007.07 7.07l1.41-1.41" stroke="currentColor" strokeWidth="2"/></svg>
        </button>
        {/* Chart */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Insert chart">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="13" width="4" height="8" fill="currentColor"/><rect x="9" y="9" width="4" height="12" fill="currentColor"/><rect x="15" y="5" width="4" height="16" fill="currentColor"/></svg>
        </button>
        {/* Filter */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Create a filter">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 5h18M6 9h12M9 13h6M10 17h4" stroke="currentColor" strokeWidth="2"/></svg>
        </button>
        {/* Functions */}
        <button className="p-2 hover:bg-gray-100 rounded" title="Functions">
          <span className="font-bold text-lg">∑</span>
        </button>
      </div>
      {/* Formula Bar Placeholder */}
      <div className="flex items-center px-2 py-1 bg-gray-50 border-b border-gray-200">
        <span className="px-2 py-1 border border-gray-300 rounded bg-white text-xs mr-2 min-w-[32px] text-center">{selectedCell ? `${String.fromCharCode(65 + selectedCell.col)}${selectedCell.row + 1}` : "A1"}</span>
        <input className="flex-1 px-2 py-1 border border-gray-300 rounded bg-white text-sm" placeholder="Enter text or formula" />
      </div>
    </div>
  );
};

export default RibbonBar; 